import { useEffect, useRef, useState } from "react";
import ReactConfetti from "react-confetti";
import { useWindowSize } from "react-use";
import { IoMdRocket } from "react-icons/io";
import Bank from "../../components/Bank/Bank";
import Typed from "typed.js";
import { useTranslation } from "react-i18next";
import ToggleSwitch from "../ToggleSwitch";
import BankBox from "../BankBox";
import SangatiQuadrants from "./SangatiQuadrants";
import useUsersResource from "../../resources/useUsersResource";
import { selectCompatibility } from "../../utils/compatibility";
import useMatchResource from "../../resources/useMatchResource";
import { RadarChartResponse } from "../../resources/types/radarTypes";

interface CompatibilityScore {
  avg?: number;
  light?: number;
  dark?: number;
}

interface GraphQuadrant {
  [key: string]: number;
}

interface GraphData {
  light?: GraphQuadrant;
  dark?: GraphQuadrant;
}

interface QuadrantsProps {
  lightmine: boolean;
  darkmine: boolean;
  lightother: boolean;
  darkother: boolean;
  islightdisabled: boolean;
  isdarkdisabled: boolean;
  showCompatibility: boolean;
  setShowCompatibility: (show: boolean) => void;
  leftToggle: boolean;
  rightToggle: boolean;
  setLeftToggle: React.Dispatch<React.SetStateAction<boolean>>;
  setRightToggle: React.Dispatch<React.SetStateAction<boolean>>;
  graphData: GraphData;
  matchee_username: string | null;
  setMatcheeGraphData: (data: RadarChartResponse) => void;
  showReadyCircle: boolean;
  setShowReadyCircle: (show: boolean) => void;
  bankbalance: number;
  sent: boolean;
  setSent: (sent: boolean) => void;
  getUserData: () => Promise<void>;
  firsttimestatus: boolean;
  compatibilityScore: CompatibilityScore;
  setCompatibilityScore: (score: CompatibilityScore) => void;
  matchNotAllowed: boolean;
  setMatchNotAllowed: (notAllowed: boolean) => void;
  modeMatch: "light_mode" | "dark_mode" | "both_mode";
  setModeMatch: (mode: "light_mode" | "dark_mode" | "both_mode") => void;
}

const Quadrants: React.FC<QuadrantsProps> = ({
  lightmine,
  darkmine,
  lightother,
  darkother,
  islightdisabled,
  isdarkdisabled,
  showCompatibility,
  setShowCompatibility,
  leftToggle,
  rightToggle,
  setLeftToggle,
  setRightToggle,
  graphData,
  matchee_username,
  setMatcheeGraphData,
  showReadyCircle,
  setShowReadyCircle,
  bankbalance,
  sent,
  setSent,
  getUserData,
  firsttimestatus,
  compatibilityScore,
  setCompatibilityScore,
  matchNotAllowed,
  setMatchNotAllowed,
  modeMatch,
  setModeMatch,
}) => {
  const [isFirstTime, setIsFirstTime] = useState<boolean>(firsttimestatus);
  const [undoTimeout, setUndoTimeout] = useState<ReturnType<
    typeof setTimeout
  > | null>(null);
  const [showConfetti, setShowConfetti] = useState<boolean>(false);
  const [launch, setLaunch] = useState<boolean>(false);
  const [startSlide, setStartSlide] = useState<boolean>(false);
  const [showBankPopup, setShowBankPopup] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasShownCelebration, setHasShownCelebration] =
    useState<boolean>(false);
  const { t } = useTranslation();
  const rawHandle = localStorage.getItem("handle");
  const handle = rawHandle ? JSON.parse(rawHandle) : null;
  const { updateUser } = useUsersResource();
  const { createMatch } = useMatchResource();
  const el = useRef<HTMLSpanElement | null>(null);
  const typed = useRef<Typed | null>(null);

  const allMine = lightmine && darkmine && leftToggle;
  const allOther = lightother && darkother && rightToggle;

  let creditsRequired = 0;

  if (allMine && allOther) {
    creditsRequired = 4;
  } else if (allMine || allOther) {
    creditsRequired = 2;
  } else {
    creditsRequired = 0;
  }

  // Toggle is enabled if:
  // - No matchee: user has completed the mode (lightmine/darkmine)
  // - Matchee selected: both user and matchee have completed the mode (lightmine && lightother / darkmine && darkother)
  const isLightToggleEnabled = () => {
    if (matchee_username !== "") {
      return lightmine && lightother;
    }
    return lightmine;
  };

  const isDarkToggleEnabled = () => {
    if (matchee_username !== "") {
      return darkmine && darkother;
    }
    return darkmine;
  };

  useEffect(() => {
    setIsFirstTime(firsttimestatus);
    if (firsttimestatus) {
      setLeftToggle(false);
      setRightToggle(false);
    }
  }, [firsttimestatus, setLeftToggle, setRightToggle]);

  const handleFirstTimeToggle = async (type: string) => {
    setShowConfetti(true);
    setLaunch(true);
    setTimeout(() => setLaunch(false), 3000);
    setTimeout(() => setShowConfetti(false), 5000);

    try {
      if (!handle) throw new Error("User handle not found");
      await updateUser(handle.user.id, {
        first_time_status: false,
        [type === "light" ? "light_mode_active" : "dark_mode_active"]: true,
      });
      
      // Update the handle in localStorage to reflect the change
      handle.user.first_time_status = false;
      localStorage.setItem("handle", JSON.stringify(handle));

      setIsFirstTime(false);
      getUserData();
    } catch (err) {
      console.error("Failed to update first time status:", err);
    }
  };

  const handleLeftToggle = async () => {
    if (!isLightToggleEnabled()) {
      return;
    }

    const newValue = !leftToggle;
    setLeftToggle(newValue);

    if (isFirstTime) {
      await handleFirstTimeToggle("light");
      return;
    }

    if (!matchee_username && handle) {
      try {
        await updateUser(handle.user.id, { light_mode_active: newValue });
        await getUserData();
      } catch (err) {
        console.error("Failed to update light mode active:", err);
      }
    }
  };

  const handleRightToggle = async () => {
    if (!isDarkToggleEnabled()) {
      return;
    }

    const newValue = !rightToggle;
    setRightToggle(newValue);

    if (isFirstTime) {
      await handleFirstTimeToggle("dark");
      return;
    }

    if (!matchee_username && handle) {
      try {
        await updateUser(handle.user.id, { dark_mode_active: newValue });
        await getUserData();
      } catch (err) {
        console.error("Failed to update dark mode active:", err);
      }
    }
  };

  useEffect(() => {
    if (sent) {
      setTimeout(() => {
        setStartSlide(true);
      }, 50);
    }
  }, [sent]);

  const handleSend = async () => {
    setSent(true);
    setStartSlide(false);
    setShowCompatibility(false);

    setTimeout(() => {
      setStartSlide(true);
    }, 50);

    const timeout = setTimeout(() => {
      setShowReadyCircle(true);
    }, 3000);

    setUndoTimeout(timeout);
  };

  const handleUndo = () => {
    if (undoTimeout) {
      clearTimeout(undoTimeout);
      setUndoTimeout(null);
    }

    setSent(false);
    setShowReadyCircle(false);
    setShowConfetti(false);
    setShowCompatibility(false);
    setStartSlide(false);
  };

  const { width, height } = useWindowSize();

  const handlecompatibility = async () => {
    if (!showReadyCircle || loading || !handle) return;

    setLoading(true);

    try {
      const res = await createMatch(handle.user.id, matchee_username || "");
      setMatcheeGraphData(res.matchee?.radar || {});
      setShowCompatibility(true);
      setCompatibilityScore({
          avg: res.match?.compatibility?.average || undefined,
          light: res.match?.compatibility?.light || undefined,
          dark: res.match?.compatibility?.dark || undefined,
      });

      if (
        res.match?.compatibility?.light !== null &&
        res.match?.compatibility?.dark !== null
      ) {
        setModeMatch("both_mode");
      } else if (res.match?.compatibility?.light !== null) {
        setModeMatch("light_mode");
      } else if (res.match?.compatibility?.dark !== null) {
        setModeMatch("dark_mode");
      }

      if (!hasShownCelebration) {
        setShowConfetti(true);
        setLaunch(true);
        setHasShownCelebration(true);

        setTimeout(() => setLaunch(false), 3000);
        setTimeout(() => setShowConfetti(false), 5000);
      }
      await getUserData();
    } catch (err: unknown) {
      console.error(err);
      if (
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response &&
        err.response.data &&
        typeof err.response.data === "object" &&
        "message" in err.response.data &&
        err.response.data.message === "Users cannot match"
      ) {
        setMatchNotAllowed(true);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setHasShownCelebration(false);
  }, [matchee_username]);

  const isMatchPossible = () => {
    const youAnsweredLight = lightmine && !islightdisabled;
    const youAnsweredDark = darkmine && !isdarkdisabled;
    const theyAnsweredLight = lightother && !islightdisabled;
    const theyAnsweredDark = darkother && !isdarkdisabled;

    return (
      (youAnsweredLight && theyAnsweredLight) ||
      (youAnsweredDark && theyAnsweredDark)
    );
  };

  const noMatch = !isMatchPossible() || matchNotAllowed;

  let displayText = "";
  if (isFirstTime) {
    displayText = t("firstTimeText");
  } else if (showCompatibility) {
    displayText = t("chanceOfCompatibility");
  } else if (bankbalance < creditsRequired || bankbalance <= 1) {
    displayText = t("insufficientCredits");
  } else if (matchee_username && noMatch) {
    displayText = t("noMatchPossible");
  } else if (showReadyCircle) {
    displayText = t("pressReadyToView");
  } else if (matchee_username) {
    displayText = t("pressSend");
  } else {
    displayText = t("enterOrScanHandle");
  }

  useEffect(() => {
    if (typed.current) {
      typed.current.destroy();
    }

    if (el.current) {
      typed.current = new Typed(el.current, {
        strings: [displayText.replace(/ /g, " ^500 ")],
        typeSpeed: 100,
        backSpeed: 80,
        loop: false,
        showCursor: false,
      });
    }

    return () => {
      typed.current?.destroy();
    };
  }, [displayText]);

  // Use selectCompatibility utility for displaying the correct percentage
  const displayedScore = selectCompatibility({
    compatibility: compatibilityScore,
    average_compatibility: compatibilityScore?.avg,
    modeMatch,
    leftToggle,
    rightToggle,
  });

  return (
    <>
      {showConfetti && (
        <div className="fixed top-0 left-0  z-50 pointer-events-none">
          <ReactConfetti width={width} height={height} numberOfPieces={100} />
        </div>
      )}

      {/* Quadrants Main */}
      <div className="flex gap-0 items-center w-full px-3 py-2">
        <div className="w-1/4 bg-[#FFFFFFB2]  font-sharetech border-[#3B3C36] text-14 border-2 select-none text-center cursor-pointer rounded-r-none p-1 h-[100px] flex justify-center items-center my-3">
          <span ref={el} className="" />
        </div>
        <div className="flex flex-col justify-center items-center relative gap-[50px] w-1/2">
          <div className="flex items-center">
            <div className="relative w-15 h-15">
              {Object.keys(graphData?.light || {}).length > 0 ? (
                <>
                  <div
                    className={`w-15 h-15 border-2 border-sangati-green border-t-0 border-r-0 ${
                      islightdisabled ? "border-none" : ""
                    }`}
                  ></div>
                  <div className={`absolute -top-6 -left-4`}>
                    <ToggleSwitch
                      isEnabled={isLightToggleEnabled()}
                      isToggled={leftToggle}
                      setIsToggled={handleLeftToggle}
                    />
                  </div>
                </>
              ) : (
                <div className="w-15 h-15 invisible" />
              )}
            </div>
            <div className="relative w-15 h-15">
              {Object.keys(graphData?.dark || {}).length > 0 ? (
                <>
                  <div
                    className={`w-15 h-15 border-2 border-sangati-green border-t-0 border-l-0 ${
                      isdarkdisabled ? "border-none" : ""
                    }`}
                  ></div>
                  <div className={`absolute -top-6 -right-4`}>
                    <ToggleSwitch
                      isEnabled={isDarkToggleEnabled()}
                      isToggled={rightToggle}
                      setIsToggled={handleRightToggle}
                    />
                  </div>
                </>
              ) : (
                <div className="w-15 h-15 invisible" />
              )}
            </div>
          </div>
          <div className="flex items-center">
            <div
              className={`w-15 h-15 relative border-2 border-sangati-green border-b-0 border-r-0 ${
                !lightother ? "border-none" : ""
              }`}
            >
              {lightother && (
                <div className="absolute bg-sangati-green h-[9px] w-[9px] rounded-full -bottom-1 -left-[5.5px]"></div>
              )}
            </div>
            <div
              className={`w-15 h-15 border-2 relative border-sangati-green border-b-0 border-l-0 ${
                !darkother ? "border-none" : ""
              }`}
            >
              {" "}
              {darkother && (
                <div className="absolute bg-sangati-green h-[9px] w-[9px] rounded-full -bottom-1 -right-[5.5px]"></div>
              )}
            </div>
          </div>{" "}
          {!showReadyCircle ? (
            <div className="absolute w-[100px] h-[100px] rounded-full border-[2px] border-[#00000033] bg-[#D9D9D9]">
              {/* Top Left */}
              {lightmine &&
                !islightdisabled &&
                (!matchee_username || lightother) && (
                  <div className="w-[38px] h-[38px] dark-bg border-sangati-green border-4 absolute top-2 left-2 rounded-tl-full"></div>
                )}

              {/* Top Right */}
              {darkmine &&
                !isdarkdisabled &&
                (!matchee_username || darkother) && (
                  <div className="w-[38px] h-[38px] light-bg border-sangati-green border-4 absolute top-2 right-2 rounded-tr-full"></div>
                )}

              {/* Bottom Left */}
              {lightother && !islightdisabled && (
                <div className="w-[38px] h-[38px] dark-bg border-sangati-green border-4 absolute bottom-2 left-2 rounded-bl-full"></div>
              )}

              {/* Bottom Right */}
              {darkother && !isdarkdisabled && (
                <div className="w-[38px] h-[38px] light-bg border-sangati-green border-4 absolute bottom-2 right-2 rounded-br-full"></div>
              )}

              {lightother && lightmine && !islightdisabled && (
                <>
                  <div
                    className={`absolute w-[20px] h-[20px] bg-[#D9D9D9] rounded-full top-1/2 -translate-y-1/2 left-[25%] ${
                      !islightdisabled
                        ? "border-[2px] border-sangati-green"
                        : ""
                    } -translate-x-1/2 z-20`}
                  />
                  <div
                    className={`absolute w-[20px] h-[20px] bg-[#D9D9D9] rounded-full top-1/2 -translate-y-1/2 left-[33%] ${
                      !islightdisabled
                        ? "border-[2px] border-sangati-green"
                        : ""
                    } -translate-x-1/2 z-20`}
                  />
                </>
              )}

              {darkother && darkmine && !isdarkdisabled && (
                <>
                  <div
                    className={`absolute w-[20px] h-[20px] bg-[#D9D9D9] rounded-full top-1/2 -translate-y-1/2 right-[12.5%] ${
                      !isdarkdisabled ? "border-[2px] border-sangati-green" : ""
                    } -translate-x-1/2 z-20`}
                  />
                  <div
                    className={`absolute w-[20px] h-[20px] bg-[#D9D9D9] rounded-full top-1/2 -translate-y-1/2 right-[4.5%] ${
                      !isdarkdisabled ? "border-[2px] border-sangati-green" : ""
                    } -translate-x-1/2 z-20`}
                  />
                </>
              )}

              {/* coin  */}
              {sent && (
                <>
                  {isdarkdisabled && islightdisabled ? null : (
                    <>
                      {!isdarkdisabled && darkmine && darkother && (
                        <>
                          <div
                            className={`absolute w-[20px] h-[20px] bg-[#C83126] border-2 border-sangati-green rounded-full top-1/2 -translate-y-1/2 z-30 transition-all ease-in-out duration-700 ${
                              startSlide ? "right-[14%]" : "-right-[48.5%]"
                            }`}
                          ></div>
                          <div
                            className={`absolute w-[20px] h-[20px] bg-[#C83126] border-2 border-sangati-green rounded-full top-1/2 -translate-y-1/2 z-30 transition-all ease-in-out duration-700 ${
                              startSlide ? "right-[22%]" : "-right-[48.5%]"
                            }`}
                          ></div>
                        </>
                      )}
                      {!islightdisabled && lightmine && lightother && (
                        <>
                          <div
                            className={`absolute w-[20px] h-[20px] bg-[#C83126] border-2 border-sangati-green rounded-full top-1/2 -translate-y-1/2 z-30 transition-all ease-in-out duration-1700 ${
                              startSlide ? "right-[64.5%]" : "-right-[42.5%]"
                            }`}
                          ></div>
                          <div
                            className={`absolute w-[20px] h-[20px] bg-[#C83126] border-2 border-sangati-green rounded-full top-1/2 -translate-y-1/2 z-30 transition-all ease-in-out duration-1700 ${
                              startSlide ? "right-[56.5%]" : "-right-[42.5%]"
                            }`}
                          ></div>
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {/* coin  */}
            </div>
          ) : (
            <SangatiQuadrants
              showCompatibility={showCompatibility}
              modeMatch={modeMatch}
              displayedScore={displayedScore}
              isLoading={loading}
              handleCompatibility={handlecompatibility}
            />
          )}
        </div>

        {!isFirstTime && (
          <div
            className={`flex flex-col items-center my-6 ${
              showCompatibility ? "bg-transparent opacity-[5%]" : "opacity-100"
            }`}
          >
            <div>
              <img
                src="/icons/Sangati/add_circle.svg"
                alt=""
                className={`h-7 w-7 ${
                  bankbalance <= 1 || bankbalance < creditsRequired
                    ? "pulse"
                    : ""
                }`}
                onClick={() => setShowBankPopup(true)}
              />
            </div>
            <div>
              <img src="/icons/Sangati/counterline.svg" alt="" className="" />
            </div>

            {/* Counter Box */}
            <BankBox
              bankbalance={bankbalance}
              creditsRequired={creditsRequired}
              matchee_username={matchee_username}
              noMatch={noMatch}
              sent={sent}
              handleUndo={handleUndo}
              handleSend={handleSend}
              showCompatibility={showCompatibility}
            />
          </div>
        )}
      </div>
      {/* Quadrants Main */}

      <div
        className={`absolute bottom-1/3 left-1/2 -translate-x-1/2
        transition-transform duration-3000 ease-out
        ${launch ? "-translate-y-[600px]" : "-translate-y-1/2"}`}
      >
        {launch ? <IoMdRocket /> : ""}
      </div>

      <div
        onClick={() => setShowBankPopup(false)}
        className={`fixed h-screen w-screen bg-[#D9D9D9]/90 flex items-end justify-center top-0 left-0 z-50 transition-transform duration-1000 ease-in-out ${
          showBankPopup ? "translate-y-0 " : "translate-y-full "
        }`}
      >
        <Bank setShowBankPopup={setShowBankPopup} getUserData={getUserData} />
      </div>
    </>
  );
};

export default Quadrants;
